/**
 * Button and tab labels, shortened to one word where the screen around them already says what it is for.
 *
 * A phone button or a tab in the bottom bar is a few finger-widths wide, and French, Arabic and Darija
 * run longer than English: "Envoyer la demande" ended in "…" on a small screen. The wording that sits
 * under a heading or beside a title does not have to repeat it, so a button says the verb alone.
 *
 * These are applied on top of the full messages when the bundle is built, one entry per key and one word
 * per language: [fr, en, ar, ma]. A key that is not already a string in every language is a typo and the
 * test fails, so this file can never invent a message by accident. The Arabic and Darija wording is a
 * draft for a native speaker to confirm.
 */
type Short = readonly [fr: string, en: string, ar: string, ma: string]

export const SHORT_LABELS: Record<string, Short> = {
  // Save, confirm, cancel, retry, back: the verb, nothing else.
  'agentOnboarding.saveProfile': ['Enregistrer', 'Save', 'حفظ', 'سجل'],
  'agentOnboarding.saveDocuments': ['Enregistrer', 'Save', 'حفظ', 'سجل'],
  'pod.validateDelivery': ['Confirmer', 'Confirm', 'تأكيد', 'أكد'],
  'agentPickups.confirmHandover': ['Confirmer', 'Confirm', 'تأكيد', 'أكد'],
  'returns.confirmDeposit': ['Confirmer', 'Confirm', 'تأكيد', 'أكد'],
  'returns.confirmCollection': ['Confirmer', 'Confirm', 'تأكيد', 'أكد'],
  'handovers.confirmDrop': ['Confirmer', 'Confirm', 'تأكيد', 'أكد'],
  'platformPickup.cancelSubmit': ['Confirmer', 'Confirm', 'تأكيد', 'أكد'],
  'platformPickup.cancelTitle': ['Annuler', 'Cancel', 'إلغاء', 'لغي'],
  'courierPickups.releasePickup': ['Annuler', 'Cancel', 'إلغاء', 'لغي'],
  'senderReturns.backToReturns': ['Retour', 'Back', 'رجوع', 'رجع'],
  'bulkPod.backToPackages': ['Retour', 'Back', 'رجوع', 'رجع'],
  'packageDetail.backToParcel': ['Retour', 'Back', 'رجوع', 'رجع'],
  'activityHistory.retry': ['Réessayer', 'Retry', 'إعادة', 'عاود'],
  'common.retry': ['Réessayer', 'Retry', 'إعادة', 'عاود'],
  'senderReturns.retry': ['Réessayer', 'Retry', 'إعادة', 'عاود'],
  'agentCreatePackage.ocrRetry': ['Réessayer', 'Retry', 'إعادة', 'عاود'],
  'common.next': ['Suivant', 'Next', 'التالي', 'الموالي'],

  // Counted actions keep the number: "Confirmer (3)" is still one word and a count.
  'handovers.confirmPackages': ['Confirmer ({count})', 'Confirm ({count})', 'تأكيد ({count})', 'أكد ({count})'],
  'courierPickups.collectConfirm': ['Récupérer ({count})', 'Collect ({count})', 'استلام ({count})', 'خود ({count})'],

  // What a person does with a parcel.
  'sync.syncNow': ['Envoyer', 'Send', 'إرسال', 'صيفط'],
  'senderReturns.handBack': ['Remettre', 'Hand over', 'تسليم', 'عطيه'],
  'actions.confirm_sender_return': ['Remettre', 'Hand over', 'تسليم', 'عطيه'],
  'senderReturns.arrived': ['Reçu', 'Got it', 'استلمته', 'خديتو'],
  'repairs.acceptPickup': ['Accepter', 'Accept', 'قبول', 'قبل'],
  'dashboard.startDrop': ['Déposer', 'Drop off', 'تسليم', 'سلّم'],
  'agentPickups.createSession': ['Collecte', 'Collection', 'جمع', 'جمع'],
  'returns.startNewDeposit': ['Un autre', 'Another', 'آخر', 'واحد آخر'],
  'returns.startNewCollection': ['Un autre', 'Another', 'آخر', 'واحد آخر'],
  'wallet.requestWithdrawal': ['Retirer', 'Withdraw', 'سحب', 'سحب'],
  'pwa.wallet.seeEarnings': ['Gains', 'Earnings', 'الأرباح', 'الربح'],
  'agentCreatePackage.createPackage': ['Créer', 'Create', 'إنشاء', 'صاوب'],
  'agentCreatePackage.autoExtract': ['Remplir', 'Autofill', 'تعبئة', 'عمر'],
  'agentCreatePackage.ocrCapture': ['Lire', 'Read', 'قراءة', 'قرا'],
  'agentCreatePackage.ocrTakePhoto': ['Photo', 'Photo', 'صورة', 'صور'],
  'agentCreatePackage.ocrUseText': ['Utiliser', 'Use', 'استعمال', 'استعمل'],
  'logisticsUi.removePhoto': ['Retirer', 'Remove', 'حذف', 'حيد'],
  'logisticsUi.removeAllPhotos': ['Vider', 'Clear', 'مسح', 'حيد'],
  'geolocation.capture': ['Position', 'Locate', 'موقعي', 'بلاصتي'],

  // Directions: the three destinations differ only by the place the screen is already about.
  'packageDetail.routeToPickup': ['Itinéraire', 'Directions', 'الاتجاهات', 'الطريق'],
  'packageDetail.routeToReturnPoint': ['Itinéraire', 'Directions', 'الاتجاهات', 'الطريق'],
  'packageDetail.routeToDrop': ['Itinéraire', 'Directions', 'الاتجاهات', 'الطريق'],

  // Scanning and reporting.
  'scanner.reportProblem': ['Signaler', 'Report', 'إبلاغ', 'بلّغ'],
  'fieldJourney.report': ['Signaler', 'Report', 'إبلاغ', 'بلّغ'],
  'journey.action.reportProblem.label': ['Signaler', 'Report', 'إبلاغ', 'بلّغ'],
  'journey.feedback.problem': ['Signaler', 'Report', 'إبلاغ', 'بلّغ'],
  'live.delivered.report': ['Signaler', 'Report', 'إبلاغ', 'بلّغ'],
  'scanner.details': ['Voir', 'View', 'عرض', 'شوف'],
  'scanner.again': ['Scanner', 'Scan', 'مسح', 'سكاني'],
  'customer.scan.again': ['Scanner', 'Scan', 'مسح', 'سكاني'],
  'fieldJourney.fullDetails': ['Tout voir', 'See all', 'عرض الكل', 'شوف كلشي'],
  'journey.fullDetails': ['Détails', 'Details', 'التفاصيل', 'التفاصيل'],

  // The customer's side.
  'platform.requests.submit': ['Envoyer', 'Send', 'إرسال', 'صيفط'],
  'platform.requests.followPackage': ['Suivre', 'Track', 'تتبع', 'تبع'],
  'platformPickup.backToPackages': ['Colis', 'Parcels', 'الطرود', 'الكوليات'],
  'platformPickup.searchArea': ['Chercher', 'Search', 'بحث', 'قلب'],
  'journey.another': ['Changer', 'Change', 'تغيير', 'بدل'],
  'journey.feedback.ok': ['Parfait', 'Great', 'ممتاز', 'مزيان'],
  'customer.scan.track': ['Suivi', 'Track', 'تتبع', 'تبع'],
  'customer.scan.askReturn': ['Retour', 'Return', 'إرجاع', 'رجوع'],
  'pricing.resend.action': ['Renvoyer', 'Resend', 'إعادة', 'عاود'],
  'repairs.payForCode': ['Payer', 'Pay', 'دفع', 'خلص'],
  'repairs.pay.showCode': ['Afficher', 'Show', 'إظهار', 'بين'],

  // Slides and sheets that answer a request.
  'live.offer.slide': ['Accepter', 'Accept', 'قبول', 'قبل'],
  'live.requestOffer.slide': ['Accepter', 'Accept', 'قبول', 'قبل'],
  'live.arrivalOffer.slide': ['Recevoir', 'Receive', 'استلام', 'استقبل'],
  'live.delivered.ok': ['Merci', 'Thanks', 'شكراً', 'شكرا'],
  'live.returnReady.open': ['Ouvrir', 'Open', 'فتح', 'حل'],

  // The bottom bar: five tabs share a few hundred pixels.
  'activityHistory.tab': ['Journal', 'History', 'السجل', 'السجل']
}

/**
 * The titles above each group of proof photos, and the captions and empty lines under them. They name who
 * did the step in a word or two ("Dépôt livreur", "Customer drop"), because a sentence like "Photos du retour
 * pris par le livreur" did not fit above a thumbnail. Each photo is captioned "Photo 1", "Photo 2": the title
 * above already says whose it is.
 */
export const SHORT_TITLES: Record<string, Short> = {
  'podMedia.operationalProofs': ['Parcours', 'Journey', 'المسار', 'المسار'],
  'podMedia.incidentProofs': ['Problème', 'Problem', 'المشكل', 'المشكل'],
  'podMedia.signedPod': ['Preuve signée', 'Signed proof', 'إثبات موقّع', 'إثبات موقّع'],

  'podMedia.courierDropPhoto': ['Dépôt livreur', 'Courier drop', 'إيداع الموزّع', 'إيداع الليفرور'],
  'podMedia.courierPickupPhoto': ['Collecte', 'Pickup', 'الجمع', 'الجمع'],
  'podMedia.receptionPhotos': ['Réception', 'Received', 'الاستلام', 'الاستلام'],
  'podMedia.deliveryPhoto': ['Livraison', 'Delivery', 'التسليم', 'التسليم'],
  'podMedia.signature': ['Signature', 'Signature', 'التوقيع', 'التوقيع'],
  'podMedia.customerHandoverPhotos': ['Dépôt client', 'Customer drop', 'إيداع الزبون', 'إيداع الكليان'],
  'podMedia.customerHandoverSignature': ['Signature client', 'Customer signature', 'توقيع الزبون', 'توقيع الكليان'],
  'podMedia.exceptionPhotos': ['Problème', 'Problem', 'المشكل', 'المشكل'],
  'podMedia.returnAgentPhotos': ['Retour agent', 'Agent return', 'إرجاع الوكيل', 'رجوع كارديان'],
  'podMedia.returnCourierPhotos': ['Retour livreur', 'Courier return', 'إرجاع الموزّع', 'رجوع ليفرور'],
  'podMedia.senderReturnPhotos': ['Remise', 'Handed back', 'تسليم للمرسل', 'تسليم للمرسل'],

  'podMedia.courierDropPhotoAlt': ['Photo {index}', 'Photo {index}', 'صورة {index}', 'تصويرة {index}'],
  'podMedia.courierPickupPhotoAlt': ['Photo {index}', 'Photo {index}', 'صورة {index}', 'تصويرة {index}'],
  'podMedia.receptionPhotoAlt': ['Photo {index}', 'Photo {index}', 'صورة {index}', 'تصويرة {index}'],
  'podMedia.customerHandoverPhotoAlt': ['Photo {index}', 'Photo {index}', 'صورة {index}', 'تصويرة {index}'],
  'podMedia.exceptionPhotoAlt': ['Photo {index}', 'Photo {index}', 'صورة {index}', 'تصويرة {index}'],
  'podMedia.returnAgentPhotoAlt': ['Photo {index}', 'Photo {index}', 'صورة {index}', 'تصويرة {index}'],
  'podMedia.returnCourierPhotoAlt': ['Photo {index}', 'Photo {index}', 'صورة {index}', 'تصويرة {index}'],
  'podMedia.senderReturnPhotoAlt': ['Photo {index}', 'Photo {index}', 'صورة {index}', 'تصويرة {index}'],

  'podMedia.noDropPhoto': ['Pas de photo', 'No photo', 'لا صورة', 'ماكاين تصويرة'],
  'podMedia.noCourierPickupPhoto': ['Pas de photo', 'No photo', 'لا صورة', 'ماكاين تصويرة'],
  'podMedia.noReceptionPhoto': ['Pas de photo', 'No photo', 'لا صورة', 'ماكاين تصويرة'],
  'podMedia.noPhoto': ['Pas de photo', 'No photo', 'لا صورة', 'ماكاين تصويرة'],
  'podMedia.noCustomerHandoverPhoto': ['Pas de photo', 'No photo', 'لا صورة', 'ماكاين تصويرة'],
  'podMedia.noExceptionPhoto': ['Pas de photo', 'No photo', 'لا صورة', 'ماكاين تصويرة'],
  'podMedia.noReturnAgentPhoto': ['Pas de photo', 'No photo', 'لا صورة', 'ماكاين تصويرة'],
  'podMedia.noReturnCourierPhoto': ['Pas de photo', 'No photo', 'لا صورة', 'ماكاين تصويرة'],
  'podMedia.noSignature': ['Pas de signature', 'No signature', 'لا توقيع', 'ماكاين توقيع'],
  'podMedia.noCustomerHandoverSignature': ['Pas de signature', 'No signature', 'لا توقيع', 'ماكاين توقيع']
}

const LOCALES = ['fr', 'en', 'ar', 'ma'] as const

type Bundle = Record<string, unknown>

function holder(bundle: Bundle, path: string[]): Bundle | null {
  let node: unknown = bundle

  for (const part of path.slice(0, -1)) {
    node = node && typeof node === 'object' ? (node as Bundle)[part] : undefined
  }

  return node && typeof node === 'object' ? (node as Bundle) : null
}

/** The bundle with every short label in place; a key that does not already exist as text is left alone. */
export function applyShortLabels<T extends Bundle>(locale: (typeof LOCALES)[number], source: T): T {
  const index = LOCALES.indexOf(locale)
  // Copied first: the locales are built from shared pieces, and writing into one must not change another.
  const bundle = structuredClone(source)

  for (const [key, labels] of Object.entries({ ...SHORT_LABELS, ...SHORT_TITLES })) {
    const path = key.split('.')
    const parent = holder(bundle, path)
    const last = path[path.length - 1]

    if (parent && typeof parent[last] === 'string') parent[last] = labels[index]
  }

  return bundle
}
